/**
 * Handler global pour les erreurs 401/JWT expirées de Supabase
 * Intercepte les erreurs d'authentification et déclenche une déconnexion propre
 */

import type { PostgrestError } from '@supabase/supabase-js'

// Patterns d'erreurs indiquant une session expirée
const AUTH_ERROR_PATTERNS = [
  'jwt expired',
  'jwt malformed',
  'invalid jwt',
  'session_not_found',
  'refresh_token_not_found',
  'token is expired',
  'pgrst301', // PostgREST JWT error
  'unauthorized'
]

/**
 * Vérifie si une erreur Supabase est liée à l'authentification expirée
 */
export function isAuthError(error: PostgrestError | Error | unknown): boolean {
  if (!error) return false

  const message = typeof error === 'object' && 'message' in error
    ? String((error as { message: unknown }).message).toLowerCase()
    : String(error).toLowerCase()

  const code = typeof error === 'object' && 'code' in error
    ? String((error as { code: unknown }).code).toLowerCase()
    : ''

  return AUTH_ERROR_PATTERNS.some(pattern =>
    message.includes(pattern) || code.includes(pattern)
  )
}

// Flag pour éviter les appels multiples simultanés (race condition)
let isHandlingAuthError = false

/**
 * Gère une erreur d'authentification expirée
 * Déclenche la déconnexion et redirige vers login
 */
export async function handleAuthError(): Promise<void> {
  // Éviter les appels multiples si plusieurs requêtes échouent simultanément
  if (isHandlingAuthError) return
  isHandlingAuthError = true

  try {
    // Import dynamique pour éviter les dépendances circulaires
    const { useAuthStore } = await import('@/stores/auth')
    const { useNotificationsStore } = await import('@/stores/notifications')

    const authStore = useAuthStore()
    const notifications = useNotificationsStore()

    // Éviter les notifications multiples si déjà déconnecté
    if (!authStore.isAuthenticated) return

    notifications.addNotification(
      'Votre session a expiré, veuillez vous reconnecter',
      'warning'
    )

    await authStore.handleSessionLost()
  } finally {
    isHandlingAuthError = false
  }
}

/**
 * Wrapper pour les appels Supabase avec gestion automatique des erreurs 401
 * Usage: const data = await withAuthErrorHandler(supabase.from('table').select())
 */
export async function withAuthErrorHandler<T>(
  promise: PromiseLike<{ data: T; error: PostgrestError | null }>
): Promise<{ data: T; error: PostgrestError | null }> {
  const result = await promise

  if (result.error && isAuthError(result.error)) {
    await handleAuthError()
  }

  return result
}
